'use client';

import { Button } from '@common/components';
import { ERROR_CODE, isNormalizedResponse } from '@core/utils';
import * as Sentry from '@sentry/nextjs';
import { randomInt } from 'es-toolkit';
import { useEffect, useRef, useState } from 'react';

import { Image } from '@/components/client';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';

interface ErrorPageProps {
  error: Error;
  reset: () => void;
}

const ERROR_IMG_PROPS = {
  alt: 'Error',
  className: 'my-40 h-auto w-full animate-wiggle',
  height: 186,
  width: 335,
} as const;

const DEPLOYMENT_ERROR_PATTERNS = [
  'module factory is not available',
  'Loading chunk',
  'ChunkLoadError',
  'CSS_CHUNK_LOAD_FAILED',
] as const;

const isDeploymentError = (error: Error) =>
  DEPLOYMENT_ERROR_PATTERNS.some((pattern) =>
    error.message?.toLowerCase().includes(pattern.toLowerCase()),
  );

const ErrorPage = ({ error }: ErrorPageProps) => {
  const router = useCustomRouter();
  const [errorId, setErrorId] = useState('');

  const errorCode = isNormalizedResponse(error) ? error.return_code : undefined;

  useEffect(() => {
    if (isDeploymentError(error)) {
      const key = 'deployment-error-reload';
      const lastReload = Number(sessionStorage.getItem(key) || 0);

      // 10초 이내 재시도 방지 (무한 루프 차단)
      if (Date.now() - lastReload > 10_000) {
        sessionStorage.setItem(key, String(Date.now()));
        window.location.reload();

        return;
      }
    }

    if (errorCode && [ERROR_CODE.HIDDEN].includes(errorCode)) return;

    const id = Sentry.captureException(error);

    setErrorId(id);
  }, [error, errorCode]);

  //! ⚠️ 2번 렌더링 되는것을 방지하기 위함
  const imgIndex = useRef(0);

  return (
    <article className="flex flex-col items-center justify-center">
      {(() => {
        switch (errorCode) {
          case ERROR_CODE.HIDDEN:
            if (!imgIndex.current) {
              imgIndex.current = randomInt(1, 2);
            }

            return (
              <>
                <section className="flex flex-1 flex-col items-center justify-center whitespace-pre-wrap p-20 text-center text-[0.875rem] text-Gray-700">
                  <h1 className="mb-24 text-[1.5rem] font-800 leading-[1.8rem] text-Gray-900">
                    해당 페이지를
                    <br />볼 수 없어요
                  </h1>
                  <p className="m-0 py-4 leading-[20px]">
                    이용 정책에 의해 비공개되었거나 삭제된 페이지에요.
                  </p>
                  <Image
                    {...ERROR_IMG_PROPS}
                    src={`/assets/pngs/error/hidden/${imgIndex.current}.png`}
                  />
                </section>
                <nav className="mt-auto flex w-full gap-8 px-20 py-40 [&>button]:flex-1">
                  <Button theme="sub-line" onClick={() => router.back()}>
                    돌아가기
                  </Button>
                  <Button onClick={() => router.push(ROUTES.HOME.pathname)}>
                    홈으로 이동
                  </Button>
                </nav>
              </>
            );

          default:
            if (!imgIndex.current) {
              imgIndex.current = randomInt(1, 2);
            }

            return (
              <>
                <section className="flex flex-1 flex-col items-center justify-center whitespace-pre-wrap p-20 text-center text-[0.875rem] text-Gray-700 [&>.inquiry-button]:font-400 [&>.inquiry-button]:text-Gray-700">
                  <h1 className="mb-24 text-[1.5rem] font-800 leading-[1.8rem] text-Gray-900">
                    생각지 못한 오류가
                    <br />
                    발생했어요
                  </h1>
                  <p className="m-0 py-4 leading-[20px]">
                    새로 고침을 선택하거나 홈으로 이동해 주세요.
                  </p>
                  <p className="m-0 py-4 leading-[20px]">
                    반복되는 오류는 고객 문의로 문의해 주시면
                    <br />
                    빠르게 처리해 드리겠습니다.
                  </p>
                  <p className="m-0 py-4 leading-[20px] text-Gray-100">{errorId}</p>
                  <Image
                    {...ERROR_IMG_PROPS}
                    src={`/assets/pngs/error/${imgIndex.current}.png`}
                  />
                </section>
                <nav className="mt-auto flex w-full gap-8 px-20 py-40 [&>button]:flex-1">
                  <Button
                    theme="sub-line"
                    onClick={() => window.location.reload()}
                    className="flex-1"
                  >
                    새로고침
                  </Button>
                  <Button asChild className="flex-1">
                    <a href={ROUTES.HOME.pathname}>홈으로 이동</a>
                  </Button>
                </nav>
              </>
            );
        }
      })()}
    </article>
  );
};

export default ErrorPage;
